import { Link, useLocation, useRouter } from "wouter";
import { useEffect, useRef, useState } from "react";
import techList from "../../data/technologies.json";
import "./TechnologyProfile.scss";
import { Close } from "../../icons/Close";
import { Tab } from "./Tab";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import { LegalLinks } from "../LegalLinks";
import { pageView } from "../../utils/analytics";

const TABS = [
	{
		title: "Grants",
		numberTemplate: "<%=value%> grants",
		valueTemplate: "<%=value%> total grant value",
		graphs: ["countGraph", "valueGraph", "choropleth"],
		prefix: "€",
		noDataMessage: "We don't have any grant data on this technology right now.",
	},
	{
		title: "Publications",
		numberTemplate: "<%=value%> publications",
		graphs: ["countGraph", "choropleth"],
		noDataMessage:
			"We don't have any publication data on this technology right now.",
	},
	{
		title: "Patents",
		numberTemplate: "<%=value%> patents",
		graphs: ["countGraph", "choropleth"],
		noDataMessage:
			"We don't have any patent data on this technology right now.",
	},
	{
		title: "Investments",
		numberTemplate: "<%=value%> investments",
		valueTemplate: "<%=value%> total investment value",
		graphs: ["countGraph", "valueGraph", "choropleth"],
		prefix: "$",
		noDataMessage:
			"We don't have any investment data on this technology right now.",
	},
];

function AccordionItem({ title, children }) {
	const [open, setOpen] = useState(false);
	return (
		<div className={"accordion-item"}>
			<button
				className={`accordion-item__header ${
					open ? "accordion-item__header--open" : ""
				}`}
				onClick={() => {
					setOpen((open) => !open);
				}}>
				<h3>{title}</h3>
			</button>
			<div
				className={"accordion-item__content"}
				style={{ display: open ? "block" : "none" }}>
				{children}
			</div>
		</div>
	);
}

export function TechnologyProfile({ slug, visible, transitionState }) {
	const containerRef = useRef();
	const titleRef = useRef();
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [activeTab, setActiveTab] = useState("grants");
	const [showNavTitle, setShowNavTitle] = useState(false);
	const [location, updateLocation] = useLocation();
	const [
		selectedComparisonTechnology,
		setSelectedComparisonTechnology,
	] = useState(null);
	const { base } = useRouter();
	let realSlug = slug || data?.slug;
	let parent = location.split("/")[1];
	let back = "/" + parent;
	let techIndex = techList.findIndex((o) => o.slug === realSlug);
	let nextLink, prevLink;
	if (techIndex !== -1 && realSlug) {
		let nextTech =
			techIndex + 1 === techList.length ? techList[0] : techList[techIndex + 1];
		let prevTech =
			techIndex - 1 < 0
				? techList[techList.length - 1]
				: techList[techIndex - 1];

		nextLink = `${back}/${nextTech.slug}`;
		prevLink = `${back}/${prevTech.slug}`;
	}
	useEffect(() => {
		if (data) {
			pageView(
				`${base}/tech-radar/${data.slug}`,
				`${data.title} - The Virtual Economy Technology Radar | L'Atelier`
			);
		}
	}, [data, base]);

	const handleLink = (e) => {
		if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey || e.button !== 0) {
			return;
		}
		if (e.target && e.target.nodeName === "A") {
			let href = e.target.getAttribute("href");
			if (href.startsWith(`${base}/${parent}`)) {
				e.preventDefault();
				updateLocation(href.replace(base, ""));
			}
		}
	};
	useEffect(() => {
		(async () => {
			if (techList.find((item) => item.slug === realSlug)) {
				const data = await import(`../../data/techs/${realSlug}.json`);
				setActiveTab(getDefaultActiveTab(data.data)?.title.toLowerCase());
				setData(data);
			}
			setLoading(false);
		})();
	}, [realSlug]);

	useEffect(() => {
		document.documentElement.classList.add("modal-active");
		disableBodyScroll(containerRef.current);
		return () => {
			clearAllBodyScrollLocks();
			document.documentElement.classList.remove("modal-active");
		};
	}, [containerRef]);

	useEffect(() => {
		let cb = (e) => {
			if (e.code === "Escape" && !e.metaKey) {
				updateLocation(back);
			}
		};
		window.addEventListener("keyup", cb);
		return () => {
			window.removeEventListener("keyup", cb);
		};
	}, [back, updateLocation]);

	useEffect(() => {
		const handleScroll = () => {
			const { scrollTop } = containerRef.current;

			const titleHeight = titleRef.current?.getBoundingClientRect().height;

			if (scrollTop >= titleHeight) {
				setShowNavTitle(true);
			} else {
				setShowNavTitle(false);
			}
		};

		handleScroll();
		containerRef.current.addEventListener("scroll", handleScroll);

		// Don't need to remove containerRef listener on dismount, as element gets removed
	}, []);

	return (
		<>
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				transition={{ duration: 0.3 }}
				className={"technology-profile-bg"}></motion.div>
			<div
				className={"technology-profile"}
				ref={containerRef}
				onClick={(e) => {
					if (e.target && e.target.classList.contains("technology-profile")) {
						updateLocation(back);
					}
				}}>
				{data && (
					<Helmet>
						<title>
							{data.title} - The Virtual Economy Technology Radar | L'Atelier
						</title>
						<meta name={"description"} content={data.summary} />
						<link rel={"canonical"} href={`${base}/tech-radar/${data.slug}`} />
					</Helmet>
				)}

				<motion.div
					className={"technology-profile__container"}
					initial={{ transform: "translateX(100%)" }}
					animate={{ transform: data ? "translateX(0%)" : "translateX(0%)" }}
					exit={{ transform: "translateX(100%)" }}
					transition={{ duration: 0.3 }}>
					<div className={"technology-profile__grid-container"}>
						<div className={"technology-profile__navigation"}>
							<div className={"technology-profile__close-container"}>
								<Link className={"technology-profile__close"} href={back}>
									<span className={"visually-hidden"}>Close</span>
									<Close />
								</Link>
							</div>
							{data && (
								<p
									className={`technology-profile__navigation__title ${
										showNavTitle
											? "technology-profile__navigation__title--show"
											: ""
									}`}
									aria-hidden={!showNavTitle}>
									{data.title}
								</p>
							)}
							{prevLink ? (
								<Link
									className={"technology-profile__previous"}
									href={prevLink}>
									← Previous
								</Link>
							) : null}
							{nextLink ? (
								<Link className={"technology-profile__next"} href={nextLink}>
									Next →
								</Link>
							) : null}
						</div>
						{data && (
							<div className={"technology-profile__content"}>
								<h1 ref={titleRef} className={"technology-profile__title"}>
									{data.title}
								</h1>
								<h2
									className={"technology-profile__subtitle"}
									dangerouslySetInnerHTML={{ __html: data.subtitle }}
								/>

								<h3 className={"technology-profile__content-label"}>
									Description:
								</h3>
								<div
									className={"technology-profile__content-block-content"}
									onClick={handleLink}
									dangerouslySetInnerHTML={{
										__html: data.description.replace(
											"/data-placeholder",
											`${base}/${parent}`
										),
									}}
								/>
								<h3 className={"technology-profile__content-label"}>
									Implications:
								</h3>
								<div
									onClick={handleLink}
									className={"technology-profile__content-block-content"}
									dangerouslySetInnerHTML={{
										__html: data.implications.replace(
											"/data-placeholder",
											`${base}/${parent}`
										),
									}}
								/>
								<div className={"technology-profile__info"}>
									<div className={"technology-profile__info-item"}>
										<h3 className={"technology-profile__info-header"}>
											Category
										</h3>
										<div className={"technology-profile__info-content"}>
											{data.category}
										</div>
									</div>
									<div className={"technology-profile__info-item"}>
										<h3 className={"technology-profile__info-header"}>
											Type of Tech
										</h3>
										<div className={"technology-profile__info-content"}>
											{data.type}
										</div>
									</div>
									<div className={"technology-profile__info-item"}>
										<h3 className={"technology-profile__info-header"}>
											Tech Opportunity Score
										</h3>
										<div className={"technology-profile__info-content"}>
											{data.score ? `${data.score}/10` : "N/A"}
										</div>
									</div>
								</div>
								{data.furtherReading && (
									<AccordionItem title={"Further Reading"}>
										<ul className={"accordion-item__list"}>
											{data.furtherReading.map((o, i) => (
												<li
													className={"accordion-item__list-item"}
													key={i}
													rel={"noreferrer"}>
													<h4 className={"accordion-item__list-header"}>
														<a
															href={o.link}
															target={"_blank"}
															rel={"noreferrer"}>
															{o.title} ↗
														</a>
													</h4>
													<div>{o.metadata}</div>
												</li>
											))}
										</ul>
									</AccordionItem>
								)}
								{data.researchEcosystem && (
									<AccordionItem title={"Research Ecosystems"}>
										<ul className={"accordion-item__list"}>
											{data.researchEcosystem.map((o, i) => (
												<li className={"accordion-item__list-item"} key={i}>
													<h4 className={"accordion-item__research-header"}>
														{o.link ? (
															<a
																href={o.link}
																target={"_blank"}
																rel={"noreferrer"}>
																{o.title} ↗
															</a>
														) : (
															o.title
														)}
													</h4>
													{o.content ? (
														<div
															onClick={handleLink}
															dangerouslySetInnerHTML={{
																__html: o.content.replace(
																	"/data-placeholder",
																	`${base}/${parent}`
																),
															}}
														/>
													) : null}
												</li>
											))}
										</ul>
									</AccordionItem>
								)}
								{data.keyPatents && (
									<AccordionItem title={"R&D - Key Patents"}>
										<ul className={"accordion-item__list"}>
											{data.keyPatents.map((o, i) => (
												<li className={"accordion-item__list-item"} key={i}>
													<h4 className={"accordion-item__rd_header"}>
														{o.link ? (
															<a
																href={o.link}
																target={"_blank"}
																rel={"noreferrer"}>
																{o.title}
															</a>
														) : (
															o.title
														)}{" "}
														/ {o.metadata}
													</h4>
													{o.content ? (
														<div
															onClick={handleLink}
															dangerouslySetInnerHTML={{
																__html: o.content.replace(
																	"/data-placeholder",
																	`${base}/${parent}`
																),
															}}
														/>
													) : null}
												</li>
											))}
										</ul>
									</AccordionItem>
								)}
							</div>
						)}
						{data && (
							<div className={"technology-profile-tabs"}>
								<div className={"technology-profile-tabs__tabs"}>
									{TABS.map((tab) => {
										let slug = tab.title.toLowerCase();
										let tabData = data.data[slug];
										let showTab = doesTabHaveData(
											tab.graphs.map((g) => tabData[g])
										);
										return showTab ? (
											<button
												key={slug}
												onClick={(e) => {
													setActiveTab(slug);
												}}
												className={`technology-profile-tabs__tab-button ${
													activeTab === slug &&
													"technology-profile-tabs__tab-button--active"
												}`}>
												{tab.title}
											</button>
										) : null;
									})}
								</div>
							</div>
						)}
						{data && (
							<div className={"technology-profile-tabs-content"}>
								<div className={"technology-profile-tabs-content__inner"}>
									{TABS.map((tab) => {
										let slug = tab.title.toLowerCase();
										let tabData = data.data[slug];
										if (
											doesTabHaveData(tab.graphs.map((g) => tabData[g])) &&
											activeTab === slug
										) {
											return (
												<Tab
													key={slug}
													title={data.title}
													numberTemplate={tab.numberTemplate}
													valueTemplate={tab.valueTemplate}
													category={tab.title}
													numberGraph={tabData.countGraph}
													valueGraph={tabData.valueGraph}
													choropleth={tabData.choropleth}
													prefix={tab.prefix || ""}
													slug={data.slug}
													noDataMessage={"Data not available."}
													selectedComparisonTechnology={
														selectedComparisonTechnology
													}
													setSelectedComparisonTechnology={
														setSelectedComparisonTechnology
													}
												/>
											);
										}
										return null;
									})}
								</div>
							</div>
						)}
						{data && (
							<div className="technology-profile__legal-links">
								<div className="technology-profile__legal-links__inner">
									<LegalLinks />
								</div>
							</div>
						)}
						{!data && !loading ? (
							<div className="technology-profile__not-found">
								<div className="technology-profile__not-found__inner">
									<h1 className="technology-profile__not-found__title">
										Page not found
									</h1>
									<div className="technology-profile__not-found__content">
										<p>
											We’re sorry, the page you are looking for was not found.
										</p>
									</div>
									<div>
										<Link href="/">
											Back to the Virtual Economy Technology Radar
										</Link>
									</div>
								</div>
							</div>
						) : null}
					</div>
				</motion.div>
			</div>
		</>
	);
}

function doesTabHaveData(graphs = []) {
	// return !!graphs.find((g) => g && g.data.length);
	return true;
}

function getDefaultActiveTab(data) {
	return TABS.find((tab) =>
		doesTabHaveData(tab.graphs.map((g) => data[tab.title.toLowerCase()][g]))
	);
}
